import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { verifyEmailSchema, VerifyEmailFormValues } from "./schema";
import AuthSubmitButton from "@/components/AuthSubmitButton";
import { Button } from "@/components/ui/button";
import { OTPInput } from "@/components/ui/OTPInput";
import moment from "moment";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useVerifyEmail } from "@/api/application-api";

export const VerifyEmailDialog = ({
  open,
  email,
  onSubmit,
  onOpenChange,
  isLoading,
}: {
  open: boolean;
  email: string;
  onSubmit: (code: string) => void;
  isLoading: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [attempts, setAttempts] = useState(1);
  const [resendTimer, setResendTimer] = useState<number | null>(null);
  const { mutate: resendVerificationCode } = useVerifyEmail();

  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm<VerifyEmailFormValues>({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: {
      code: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    const inputElement = document.querySelector(
      '[data-tid="verify-email-input-0"]',
    );

    if (inputElement) {
      (inputElement as HTMLInputElement).focus();
    }

    return () => {
      reset();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resendTimer === null || resendTimer <= 0) return;

    const intervalId = setInterval(() => {
      setResendTimer((prevTimer) =>
        prevTimer !== null && prevTimer > 0 ? prevTimer - 1 : null,
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, [resendTimer]);

  const handleResendCode = () => {
    resendVerificationCode({
      email,
    });

    setResendTimer(60 * attempts);
    setAttempts((prev) => prev + 1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-black/30 z-50"
        className="z-[51] sm:max-w-[425px]"
      >
        <DialogHeader className="text-center">
          <div className="flex items-center gap-4">
            <div className="flex flex-col text-center">
              <DialogTitle className="text-xl">Verification Code</DialogTitle>
              <DialogDescription>
                We've sent a verification code to{" "}
                <span className="font-bold">{email}</span>. Please enter it
                below.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((data) => onSubmit(data.code))}
        >
          <div className="mt-4">
            <OTPInput
              code={watch("code")}
              setCode={(value) =>
                setValue("code", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              errorMessage={errors.code?.message}
            />
          </div>
          <div className="space-y-4">
            <AuthSubmitButton
              loading={isLoading}
              disabled={!isValid}
              text="Verify Email"
            />
            <div className="text-center text-sm text-muted-foreground">
              Didn't receive a code?{" "}
              <Button
                variant="inlineLink"
                onClick={handleResendCode}
                disabled={resendTimer !== null && resendTimer > 0}
                className="h-auto p-0 font-medium"
              >
                {resendTimer !== null && resendTimer > 0
                  ? `Resend Code (${moment
                      .utc(resendTimer * 1000)
                      .format("m:ss")})`
                  : "Resend Code"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
